import { ROUTES, withLocale } from "@/config/routes";
import { useBalanceUpdater } from "@/features/common/socket";
import { accountType } from "@/shared/api/browser/accountType";
import { getCleanPathname } from "@/shared/lib/getCleanPathname";
import { pendingBetsStore } from "@/shared/stores/PendingBetsStore";
import { userStore } from "@/shared/stores/UserStore";
import { Cross, Down, Muslim } from "@cronika/assets/icons";
import { useTranslation } from "@cronika/locales";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, cn } from "@cronika/ui";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

type BalanceDropdownProps = {
  variant?: "desktop" | "mobile";
};

export const BalanceDropdown: FC<BalanceDropdownProps> = observer(({ variant = "desktop" }) => {
  const { t } = useTranslation("layout");
  const location = useLocation();
  const navigate = useNavigate();

  const isHorizontal = useMediaQuery("(max-height: 390px)");

  const balance = userStore.balance;
  const tradingBalance = userStore.tradingBalance;

  const [balanceType, setBalanceType] = useState<"demo" | "real" | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const isWalletPage = useMemo(() => {
    const cleanPathname = getCleanPathname(location.pathname);
    return cleanPathname === ROUTES.WALLET;
  }, [location.pathname]);

  useBalanceUpdater({ enabled: true });

  useEffect(() => {
    userStore.refreshBalance();
  }, []);

  const handleChangeAccountType = useCallback((type: "demo" | "real") => {
    setBalanceType(type);
    accountType.set(type);
    userStore.refreshBalance();
  }, []);

  useEffect(() => {
    if (isWalletPage) {
      setBalanceType("real");
      return;
    }

    const currentAccountType = accountType.get();
    if (currentAccountType === "real") {
      setBalanceType("real");
    } else {
      setBalanceType("demo");
      if (!currentAccountType) {
        accountType.set("demo");
      }
    }

    return accountType.subscribe((type) => {
      if (!isWalletPage) {
        setBalanceType(type);
      }
    });
  }, [isWalletPage]);

  const displayBalanceType = isWalletPage ? "real" : balanceType;
  const rawDisplayBalance = balance
    ? displayBalanceType === "demo"
      ? Number(balance.demoBalance)
      : tradingBalance
    : 0;
  const displayBalance = Math.max(0, rawDisplayBalance - pendingBetsStore.optimisticPendingAmount);

  const [displayedBalance, setDisplayedBalance] = useState(displayBalance);
  const displayedBalanceRef = useRef(displayBalance);
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    const from = displayedBalanceRef.current;
    const to = displayBalance;

    if (Math.abs(from - to) < 0.005) return;

    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
    }

    const duration = 500;
    const startTime = performance.now();
    const startFrom = displayedBalanceRef.current;

    const animate = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startFrom + (to - startFrom) * eased;
      displayedBalanceRef.current = current;
      setDisplayedBalance(current);

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        animFrameRef.current = null;
        displayedBalanceRef.current = to;
        setDisplayedBalance(to);
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    };
  }, [displayBalance]);

  if (!balance) return null;

  const streamMode = userStore.user?.streamMode ?? false;

  const formatUsd = (value: number | string) =>
    new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));

  const AccountRow: FC<{ label: string; amount: number | string; active?: boolean }> = ({ label, amount }) => (
    <div className="flex items-center gap-4">
      <div className="flex flex-col items-start gap-1">
        <p className="text-[12px] leading-[100%]">{label}</p>
        <p className="text-[10px]  leading-[100%]">{streamMode ? "$ ***" : `$ ${formatUsd(amount)}`}</p>
      </div>
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu open={isWalletPage ? false : isOpen} onOpenChange={isWalletPage ? undefined : setIsOpen}>
        <DropdownMenuTrigger asChild>
          {variant === "mobile" ? (
            <button type="button" className="flex flex-col items-center leading-none cursor-pointer">
              <p className="text-[16px] text-white leading-tight">
                {streamMode ? "$ ***" : `$ ${formatUsd(displayedBalance)}`}
              </p>
              <div className="flex items-center">
                <p className="text-[12px] text-warning leading-tight">
                  {displayBalanceType === "demo" ? t("header.demo-account-label") : t("header.real-account-label")}
                </p>
                {!isWalletPage && (
                  <Down
                    className="text-[#E5FFFF8F] transition-transform duration-200"
                    style={isOpen ? { transform: "rotate(180deg)" } : undefined}
                  />
                )}
              </div>
            </button>
          ) : (
            <Button
              variant="flat"
              size="md"
              className={cn(
                "w-full sm:w-fit md:h-12 bg-[#8C8C8C]/15 rounded-xl",
                isHorizontal ? "max-h-8 px-1.25 py-1.25" : "px-2",
              )}
            >
              {userStore.user?.halalEnabled && (
                <span
                  className={cn(
                    "shrink-0 mr-2 flex items-center justify-center rounded-lg bg-[#F2F2F214] p-2",
                    isHorizontal && "w-5.5 h-5.5 p-1.5",
                  )}
                >
                  <Muslim className="text-[#F4F4F4] opacity-30 w-3 h-3" />
                </span>
              )}
              <div className="flex flex-col items-start leading-none">
                <p
                  className={cn(
                    "text-[16px] text-white leading-tight flex items-center gap-2ing-3",
                    isHorizontal ? "text-[12px] leading-3" : "text-[16px]",
                  )}
                >
                  {streamMode ? "$ ***" : `$ ${formatUsd(displayedBalance)}`}
                </p>
                <div className="flex items-center">
                  <p className={cn("text-warning leading-tight", isHorizontal ? "text-[10px]" : "text-[12px]")}>
                    {displayBalanceType === "demo" ? t("header.demo-account-label") : t("header.real-account-label")}
                  </p>
                  {!isWalletPage && (
                    <Down
                      className="text-[#E5FFFF8F] transition-transform duration-200"
                      style={isOpen ? { transform: "rotate(180deg)" } : undefined}
                    />
                  )}
                </div>
              </div>
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          size="md"
          align="start"
          radius="xl"
          className="border-white/10 bg-[#F2F2F214] backdrop-blur-md p-4 w-80"
        >
          <div className="flex items-center justify-between mb-3 px-2">
            <p className="text-[27px] font-semibold text-white">Accounts</p>
            <DropdownMenuItem asChild className="p-0 m-0 w-auto h-auto focus:bg-transparent">
              <button type="button" className="p-1">
                <Cross className="w-6 h-6 text-white" />
              </button>
            </DropdownMenuItem>
          </div>
          <DropdownMenuItem
            onSelect={() => handleChangeAccountType("demo")}
            className={cn("rounded-lg mb-4 hover:bg-[#FFFFFF1A]", displayBalanceType === "demo" && "bg-[#FFFFFF1A]")}
          >
            <div className="flex items-center gap-3 w-full">
              <span className="flex shrink-0 items-center justify-center w-7 h-7 rounded-lg bg-warning">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="13" viewBox="0 0 16 13" fill="none">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.4375 3.675H9.32187C9.32187 3.675 11.4531 3.93125 11.4531 6.0875C11.4531 8.24375 9.36875 8.54688 9.36875 8.54688H6.4375V3.675ZM3.32812 12.1625V12.1781H9.69687C9.69687 12.1781 12.5781 11.9438 14.1687 10.0938C15.7594 8.24375 15.7406 6.18125 15.7406 6.18125C15.7406 6.18125 15.95 3.44063 13.7281 1.52188C12.4812 0.446875 11.0219 0.096875 9.97812 0H8.6L6.44062 0.00937498H0V3.61562H2.55313V12.1625H3.32812Z"
                    fill="black"
                  />
                </svg>
              </span>
              <AccountRow
                label={t("header.demo-account-label")}
                amount={balance.demoBalance}
                active={displayBalanceType === "demo"}
              />
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem
            onSelect={() => handleChangeAccountType("real")}
            className={cn("rounded-lg hover:bg-[#FFFFFF1A]", displayBalanceType === "real" && "bg-[#FFFFFF1A]")}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <span
                  className="flex shrink-0 items-center justify-center w-7 h-7 rounded-lg"
                  style={{ background: "radial-gradient(47.58% 56.65% at 47.64% 51.63%, #01FFAF 0%, #00C395 100%)" }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none">
                    <path
                      d="M9.04581 12.6047C8.43034 13.2251 7.62907 13.6435 6.69425 13.8551C6.28781 13.9465 6.10201 14.1244 6.12523 14.4755C6.14265 14.8218 6.12523 15.1632 6.11943 15.5095C6.11943 15.8173 5.92782 15.9808 5.56202 15.9904C5.32396 15.9952 5.0859 16 4.84784 16C4.63882 16 4.42979 16 4.22076 15.9952C3.82593 15.9904 3.64013 15.8028 3.64013 15.4854C3.63432 15.2353 3.63432 14.9805 3.63432 14.7304C3.62852 14.1725 3.60529 14.1533 2.96079 14.0667C2.13629 13.9561 1.32341 13.8022 0.568586 13.4992C-0.0236587 13.2588 -0.0875288 13.1386 0.0808545 12.6192C0.208594 12.2344 0.336333 11.8497 0.481491 11.4698C0.586005 11.1909 0.684712 11.0658 0.864708 11.0658C0.969222 11.0658 1.10277 11.1091 1.28276 11.1861C2.11887 11.5467 3.00724 11.7487 3.94206 11.8449C4.09883 11.8593 4.2556 11.869 4.41237 11.869C4.84784 11.869 5.27171 11.8016 5.68395 11.6525C6.72329 11.2774 6.88586 10.2819 6.00911 9.6856C5.71299 9.48362 5.37041 9.33454 5.01623 9.20469C4.10463 8.87286 3.15821 8.62278 2.29887 8.19477C0.905353 7.50225 0.0227915 6.55485 0.127305 5.15059C0.243432 3.56357 1.32921 2.57289 3.08853 2.04388C3.81432 1.82747 3.82013 1.83228 3.82013 1.20709C3.82013 0.995492 3.81432 0.783889 3.82593 0.567478C3.84335 0.0961829 3.93625 0.0144274 4.50527 0C4.56914 0 4.63882 0 4.70269 0C4.81301 0 4.92333 0 5.03365 0C5.0801 0 5.12655 0 5.16719 0C6.24717 0 6.24717 0.0384732 6.25297 1.00511C6.25878 1.71686 6.25878 1.71686 7.11231 1.82747C7.76842 1.91404 8.3897 2.07274 8.99356 2.29396C9.32452 2.41419 9.45226 2.60655 9.34774 2.89991C9.19678 3.33273 9.05162 3.77036 8.88904 4.19838C8.78453 4.45807 8.68582 4.5783 8.50002 4.5783C8.39551 4.5783 8.26777 4.54463 8.10519 4.47731C7.26908 4.14067 6.39233 3.97716 5.47493 3.97716C5.3588 3.97716 5.23687 3.98197 5.12074 3.98678C4.84784 4.0012 4.58075 4.03006 4.32527 4.12143C3.41949 4.44845 3.27433 5.27562 4.04657 5.78539C4.4356 6.04509 4.88268 6.22783 5.34138 6.38653C6.14265 6.66066 6.94393 6.92516 7.70456 7.27142C10.0968 8.37752 10.7471 10.8927 9.04581 12.6047Z"
                      fill="black"
                    />
                  </svg>
                </span>
                <AccountRow
                  label={t("header.real-account-label")}
                  amount={tradingBalance}
                  active={displayBalanceType === "real"}
                />
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-[#17C96440]"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(withLocale(ROUTES.WALLET) + "?tab=deposit");
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 17 15" fill="none">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M13.3333 0H3.33333C1.5 0 0 1.5 0 3.33333C0 4.75 0.833333 5.91667 2.08333 6.41667V3.33333C2.08333 3 2.25 2.66667 2.41667 2.41667C2.58333 2.16667 3 2.08333 3.33333 2.08333H13.3333C13.6667 2.08333 14 2.25 14.25 2.41667C14.5 2.58333 14.5833 3 14.5833 3.33333V6.41667C15.8333 5.91667 16.6667 4.75 16.6667 3.33333C16.6667 1.5 15.1667 0 13.3333 0ZM13.3333 13.3333V3.33333H3.33333V13.3333C3.33333 14.25 4.08333 15 5 15H11.6667C12.5833 15 13.3333 14.25 13.3333 13.3333ZM7.66667 7.83333L7.08333 8.41667C6.83333 8.66667 6.41667 8.66667 6.16667 8.41667C5.91667 8.16667 5.91667 7.75 6.16667 7.5L7.25 6.41667C7.83333 5.83333 8.75 5.83333 9.33333 6.41667L10.4167 7.5C10.6667 7.75 10.6667 8.16667 10.4167 8.41667C10.1667 8.66667 9.75 8.66667 9.5 8.41667L8.91667 7.83333V11.3333C8.91667 11.6667 8.66667 12 8.25 12C7.83333 12 7.58333 11.75 7.58333 11.3333V7.83333H7.66667Z"
                      fill="#17C964"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});
